import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import { BaseModalComponent } from './base-modal.component';
import { Activity } from '../../../core/services/admin.service';
import { AppDatepickerComponent } from '../datepickers/io-flatpickr/io-flatpickr.component';

export interface ActivityNewPriceDialogData {
  activity: Activity;
  projectLabel: string;
  employeeLabel: string;
  unitLabel: string;
  costcenterLabel: string;
}

export interface ActivityNewPriceDialogResult {
  new_name: string;
  new_price: number;
  new_start_date: string;
}

@Component({
  selector: 'app-activity-new-price-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, AppDatepickerComponent],
  template: require('./activity-new-price-modal.component.html').default,
  styles: [
    require('!!raw-loader!sass-loader!./base-modal.component.scss').default,
    require('!!raw-loader!sass-loader!./activity-new-price-modal.component.scss').default,
  ],
  animations: [BaseModalComponent.modalAnimations[0]],
})
export class ActivityNewPriceModalComponent extends BaseModalComponent {
  currentActivity!: Activity;
  projectLabel = '';
  employeeLabel = '';
  unitLabel = '';
  costcenterLabel = '';

  newName = '';
  newPrice: number | null = null;
  newStartDateMoment: moment.Moment | null = null;

  configure(data: ActivityNewPriceDialogData): void {
    this.currentActivity = data.activity;
    this.projectLabel = data.projectLabel;
    this.employeeLabel = data.employeeLabel;
    this.unitLabel = data.unitLabel;
    this.costcenterLabel = data.costcenterLabel;

    this.newName = data.activity.activity_name;
    this.newPrice = data.activity.price;
    this.newStartDateMoment = this.getDefaultStartDate(data.activity);
  }

  protected override focusFirstElement(): void {
    const input = document.getElementById('activity-new-price-name');
    if (input) {
      input.focus();
    }
  }

  onNewStartDateChange(newDate: moment.Moment | null): void {
    this.newStartDateMoment = newDate;
  }

  getCurrentPriceLabel(): string {
    if (this.currentActivity.price == null) {
      return 'Inte satt';
    }
    return this.formatNumber(this.currentActivity.price);
  }

  getNewStopDateLabel(): string {
    if (!this.newStartDateMoment) {
      return '-';
    }

    if (this.currentActivity.stop_date && this.newStartDateMoment.format('YYYY-MM-DD') <= this.currentActivity.stop_date) {
      return this.currentActivity.stop_date;
    }

    return '-';
  }

  getOldStopDateLabel(): string {
    if (!this.newStartDateMoment) {
      return this.currentActivity.stop_date || '-';
    }

    return this.newStartDateMoment.clone().subtract(1, 'day').format('YYYY-MM-DD');
  }

  canSave(): boolean {
    return !this.validationMessage();
  }

  validationMessage(): string {
    if (!this.newName.trim()) {
      return 'Nytt aktivitetsnamn måste fyllas i.';
    }

    if (this.newPrice == null || !Number.isFinite(Number(this.newPrice))) {
      return 'Nytt pris måste vara ett giltigt tal.';
    }

    if (this.currentActivity.price != null && Number(this.newPrice) === Number(this.currentActivity.price)) {
      return 'Nytt pris måste skilja sig från nuvarande pris.';
    }

    const newStartDate = this.getNewStartDate();
    if (!newStartDate) {
      return 'Gäller från och med måste fyllas i.';
    }

    if (newStartDate <= this.currentActivity.start_date) {
      return 'Nytt startdatum måste vara senare än aktivitetens nuvarande startdatum.';
    }

    if (this.currentActivity.stop_date) {
      const latestAllowed = moment(this.currentActivity.stop_date).add(1, 'day').format('YYYY-MM-DD');
      if (newStartDate > latestAllowed) {
        return 'Nytt startdatum får inte skapa ett glapp efter aktivitetens nuvarande slutdatum.';
      }
    }

    return '';
  }

  save(): void {
    const validationMessage = this.validationMessage();
    if (validationMessage) {
      return;
    }

    this.confirm({
      new_name: this.newName.trim(),
      new_price: Number(this.newPrice),
      new_start_date: this.getNewStartDate()!,
    } satisfies ActivityNewPriceDialogResult);
  }

  private getNewStartDate(): string | null {
    return this.newStartDateMoment ? this.newStartDateMoment.format('YYYY-MM-DD') : null;
  }

  private getDefaultStartDate(activity: Activity): moment.Moment | null {
    if (!activity.stop_date) {
      return null;
    }

    return moment(activity.stop_date).add(1, 'day');
  }

  private formatNumber(value: number): string {
    return value.toLocaleString('sv-SE', {
      useGrouping: false,
      maximumFractionDigits: 2,
    });
  }
}
